import React, { useState, useRef, useCallback } from "react";
import DashboardLayout from "../../../Layout/DashboardLayout";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import { API_ROUTE } from '../../../constants/workerConstants';
import AxiosInstance from '../../../services/http.handlers';
import { downloadFilteredExcel, getFilteredData, getFilteredDataBySystemId } from '../../../services/invTreamentUsageDoc.handlers';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';

// Remove the global style that was causing issues
// const style = document.createElement('style');
// style.textContent = `
//   body {
//     padding-right: 0 !important;
//     overflow-y: scroll !important;
//   }
// `;
// document.head.appendChild(style);

const TableRowData = [
  "System ID",
  "Date",
  "Last Connect Time",
  "Last IP Address",
  "Partial Treatment Count",
  "Total Treatment Count"
];

const allTimeStartDate = '2023-11-01';
const getAllTimeEndDate = () => moment().endOf('day');

const InvoiceRules = () => {
  const navigate = useNavigate();
  const [data, setData] = React.useState([]); // Empty array instead of dummy data
  const [loading, setLoading] = React.useState(false);
  const [downloadingExcel, setDownloadingExcel] = React.useState(false);
  const [page, setPage] = React.useState(0); // MUI pagination is 0-based
  const [rowsPerPage, setRowsPerPage] = React.useState(10); // Default to 10 rows per page
  
  // State for date filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Refs for date inputs
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const [dateRangeType, setDateRangeType] = useState('allTime');
  const [totalRecords, setTotalRecords] = useState(0);
  // System ID state
  const [systemId, setSystemId] = useState('');
  // State to track if date filter is active (true by default)
  const [dateFilterActive, setDateFilterActive] = useState(true);
  // State to track if a search has been performed
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Popover state
  const open = Boolean(anchorEl);
  
  // Handle date range type change
  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);
    
    const now = moment();
    let start = null;
    let end = null;
    
    switch(type) {
      case 'day':
        start = now.clone().startOf('day');
        end = now.clone().endOf('day');
        break;
      case 'week':
        start = now.clone().startOf('week');
        end = now.clone().endOf('week');
        break;
      case 'month':
        start = now.clone().startOf('month');
        end = now.clone().endOf('month');
        break;
      case 'allTime':
        start = moment(allTimeStartDate);
        end = getAllTimeEndDate();
        break;
      default:
        break;
    }
    
    setStartDate(start);
    setEndDate(end);
    
    // Reset to first page when changing date range type
    setPage(0);
    
    // Make sure date filter is active when changing date range
    setDateFilterActive(true);
    
    // Fetch data with the new date range, starting from page 1
    const safeStart = start && start.isValid() ? start : null;
    const safeEnd = end && end.isValid() ? end : null;
    fetchData(safeStart, safeEnd, 1, rowsPerPage, systemId);
  };
  
  // Handle date picker open
  const handleCalendarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle date picker close
  const handleCalendarClose = () => {
    setAnchorEl(null);
  };
  
  // Date state is changed directly via Material-UI DatePicker components
  
  // Handle Apply button click
  const handleApplyDates = () => {
    // Reset to first page when applying new dates
    setPage(0);
    
    // Hide the calendar popover
    handleCalendarClose();
    
    // Fetch data with the new date range
    fetchData(startDate, endDate, 1, rowsPerPage);
  };
  
  // Handle system ID change
  const handleSystemIdChange = (event) => {
    const newValue = event.target.value;
    
    // Update state with new value
    setSystemId(newValue);
    
    // If the search box is cleared (becomes empty), immediately remove the filter
    if (newValue === '') {
      // Restore date filtering if it was disabled
      setDateFilterActive(true);
      // Reset search performed state
      setSearchPerformed(false);
      
      // Reset to first page when clearing search
      setPage(0);
      // Pass empty string directly to ensure we're using the current value
      fetchData(startDate, endDate, 1, rowsPerPage, '');
    }
  };

  // Handle system ID search
  const handleSystemIdSearch = () => {
    // Reset to first page when searching
    setPage(0);
    
    // Mark that a search has been performed if systemId is not empty
    const trimmedId = systemId.trim();
    if (trimmedId !== '') {
      setSearchPerformed(true);
      
      // When searching for a system ID, determine which date parameters to pass
      // based on the current dateFilterActive state
      if (dateFilterActive) {
        // If date filter is active, use the current date range
        const safeStartDate = startDate && startDate.isValid() ? startDate : null;
        const safeEndDate = endDate && endDate.isValid() ? endDate : null;
        fetchData(safeStartDate, safeEndDate, 1, rowsPerPage, trimmedId);
      } else {
        // If date filter is inactive, pass undefined to omit date parameters
        fetchData(undefined, undefined, 1, rowsPerPage, trimmedId);
      }
    } else {
      // For empty system ID, reset search performed state
      setSearchPerformed(false);
      
      // Use current date filter state
      if (dateFilterActive) {
        const safeStartDate = startDate && startDate.isValid() ? startDate : null;
        const safeEndDate = endDate && endDate.isValid() ? endDate : null;
        fetchData(safeStartDate, safeEndDate, 1, rowsPerPage, '');
      } else {
        fetchData(undefined, undefined, 1, rowsPerPage, '');
      }
    }
    
    // Close the popover
    setAnchorEl(null);
  };

  // Format date range for display
  const getFormattedDateRange = () => {
    if (!dateFilterActive) {
      return 'No date filter';
    }
    
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) {
      return 'Select dates';
    }
    
    return `${startDate.format('MMM DD, YYYY')} - ${endDate.format('MMM DD, YYYY')}`;
  };

  // Format date for table display
  const getFormattedDateForTable = (dateValue) => {
    // If no date value provided, return empty string
    if (!dateValue) return '';
    
    // Parse the date with moment
    const date = moment(dateValue);
    if (!date.isValid()) return '';
    
    // If date filter is not active, just show the created date
    if (!dateFilterActive) {
      return date.format('YYYY/MM/DD');
    }
    
    // With date filter active, show the date range
    // Only show single date if start and end dates are the same
    if (startDate && endDate && startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD')) {
      return date.format('YYYY/MM/DD');
    }
    
    // For different start/end dates, show the date range
    if(startDate && endDate){
      return `${startDate.format('YYYY/MM/DD')} - ${endDate.format('YYYY/MM/DD')}`;
    } else {
      return date.format('YYYY/MM/DD');
    }
  };

  // Extract connectivity info from row data
  const extractConnectivityInfo = (row) => {
    // Handle both uppercase and lowercase property names
    const connectivityData = row?.Connectivity || row?.connectivity;
    
    if (connectivityData) {
      return {
        // Handle both uppercase and lowercase property names within the object
        publicIp: connectivityData.PublicIp || connectivityData.publicIp || 'N/A',
        lastConnectionTime: connectivityData.LastConnectionTime || connectivityData.lastConnectionTime || null
      };
    }
    
    return { publicIp: 'N/A', lastConnectionTime: null };
  };
  
  // Format last connection time
  const formatLastConnectionTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Format the timestamp in a nicer way with GMT
      return moment(timestamp).format('MMM DD, YYYY HH:mm:ss') + ' GMT';
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return 'Invalid Date';
    }
  };

  // Handler to remove date filter
  const handleRemoveDateFilter = () => {
    // Update state to indicate date filter is inactive
    setDateFilterActive(false);
    
    // Reset to first page
    setPage(0);
    
    // Get the current systemId (trimmed)
    const trimmedId = systemId.trim();
    
    // When removing date filter, don't send date parameters at all
    // We use undefined instead of null to ensure they're completely omitted from the request
    fetchData(undefined, undefined, 1, rowsPerPage, trimmedId);
  };

  // Restore date filter
  const handleRestoreDateFilter = () => {
    // Update state to indicate date filter is active
    setDateFilterActive(true);
    setDateRangeType('allTime');
    
    // Restore the default custom date range
    const start = moment(allTimeStartDate);
    const end = getAllTimeEndDate();
    
    // Update the date state
    setStartDate(start);
    setEndDate(end);
    
    // Reset to first page
    setPage(0);
    
    // Get the current systemId (trimmed) for consistent handling
    const trimmedId = systemId.trim();
    
    // Fetch data with restored date filter and current system ID
    fetchData(start, end, 1, rowsPerPage, trimmedId);
  };

  // Fetch data from API
  // Add optional sysId parameter with default to current state
  const fetchData = useCallback(async (start, end, pageNumber, pageSize, sysId = systemId) => {
    try {
      setLoading(true);
      
      // FIXED: Don't rely solely on dateFilterActive which might be stale
      // Instead, check if valid date parameters were explicitly provided
      const hasValidDates = start && end && start.isValid() && end.isValid();
      let response;
      
      // Choose the appropriate API call based on whether systemId is provided
      const trimmedId = sysId ? sysId.trim() : '';
      
      if (sysId && trimmedId !== '') {
        try {
          // Just use the system ID as-is without zero padding (backend will handle it)
          if (hasValidDates) {
            // With date filter - use regular API
            const formattedStartDate = start.format('YYYY-MM-DD');
            const formattedEndDate = end.format('YYYY-MM-DD');
            
            response = await getFilteredDataBySystemId(
              trimmedId,
              formattedStartDate, 
              formattedEndDate, 
              parseInt(pageNumber, 10), 
              parseInt(pageSize, 10)
            );
          } else {
            // Without date filter - make direct axios call to omit date params completely
            const url = `/${API_ROUTE.InvTreamentUsageDoc.FILTERED_BY_SYSID}?systemId=${encodeURIComponent(trimmedId)}&pageNumber=${parseInt(pageNumber, 10)}&pageSize=${parseInt(pageSize, 10)}`;
            response = await AxiosInstance.get(url);
          }
        } catch (error) {
          // Handle error with system ID silently
          
          // Fallback to no systemId filter if there's an error
          if (hasValidDates) {
            // With date filter - use regular API
            const formattedStartDate = start.format('YYYY-MM-DD');
            const formattedEndDate = end.format('YYYY-MM-DD');
            
            response = await getFilteredData(
              formattedStartDate, 
              formattedEndDate, 
              parseInt(pageNumber, 10), 
              parseInt(pageSize, 10)
            );
          } else {
            // Without date filter - make direct axios call to omit date params completely
            const url = `/${API_ROUTE.InvTreamentUsageDoc.FILTERED_DATA}?pageNumber=${parseInt(pageNumber, 10)}&pageSize=${parseInt(pageSize, 10)}`;
            response = await AxiosInstance.get(url);
          }
        }
      } else {
        // No systemId provided - use regular filtered data endpoint
        if (hasValidDates) {
          // With date filter - use regular API
          const formattedStartDate = start.format('YYYY-MM-DD');
          const formattedEndDate = end.format('YYYY-MM-DD');
          
          response = await getFilteredData(
            formattedStartDate, 
            formattedEndDate, 
            parseInt(pageNumber, 10), 
            parseInt(pageSize, 10)
          );
        } else {
          // Without date filter - make direct axios call to omit date params completely
          const url = `/${API_ROUTE.InvTreamentUsageDoc.FILTERED_DATA}?pageNumber=${parseInt(pageNumber, 10)}&pageSize=${parseInt(pageSize, 10)}`;
          response = await AxiosInstance.get(url);
        }
      }
      
      // Process API response
      
      if (response && response.data) {
        // Get the data from response
        const rows = response.data.Data || response.data.data || [];
        
        // Process each row to extract connectivity info
        const processedData = rows.map(row => {
          // Extract connectivity info from the row
          const connectivityInfo = extractConnectivityInfo(row);
          
          // Create the processed row with connectivity info
          const processedRow = {
            ...row,
            lastConnectionTime: connectivityInfo.lastConnectionTime,
            publicIp: connectivityInfo.publicIp
          };
          
          // Process row data
          return processedRow;
        });
        
        // Set the processed data
        setData(processedData);
        
        // Handle pagination data
        const total = response.data.TotalRecords || response.data.totalCount || response.data.totalRecords || 0;
        setTotalRecords(total);
      } else {
        // Handle invalid response
        setData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      // Handle error silently
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [systemId]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    
    // API pagination is 1-based, UI pagination is 0-based
    const apiPageNumber = newPage + 1;
    
    // Only include date parameters if the date filter is active
    if (dateFilterActive) {
      fetchData(startDate, endDate, apiPageNumber, rowsPerPage);
    } else {
      // Pass undefined for dates when filter is inactive to omit them from the request
      fetchData(undefined, undefined, apiPageNumber, rowsPerPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    
    
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page
    
    // Only include date parameters if the date filter is active
    if (dateFilterActive) {
      fetchData(startDate, endDate, 1, newRowsPerPage);
    } else {
      // Pass undefined for dates when filter is inactive to omit them from the request
      fetchData(undefined, undefined, 1, newRowsPerPage);
    }
  };

  // Custom rows per page selector
  const CustomRowsPerPageSelector = () => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', marginRight: '24px' }}>
        <Typography variant="body2" sx={{ marginRight: '8px' }}>
          Rows per page:
        </Typography>
        <FormControl size="small">
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            displayEmpty
            inputProps={{ 'aria-label': 'rows per page' }}
            sx={{ minWidth: 70 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  };

  // Handle Excel download
  const handleExcelDownload = () => {
    // Only check for dates if date filter is active
    if (dateFilterActive && (!startDate || !endDate)) {
      return;
    }

    setDownloadingExcel(true);

    // Get token from session storage
    const token = sessionStorage.getItem("token");

    // Set up download options
    const downloadOptions = {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    // Determine whether to include date parameters
    let formattedStartDate, formattedEndDate;
    let trimmedSystemId = systemId && systemId.trim() !== '' ? systemId.trim() : null;
    
    if (dateFilterActive) {
      // Include date parameters when filter is active
      formattedStartDate = startDate.format('YYYY-MM-DD');
      formattedEndDate = endDate.format('YYYY-MM-DD');
      
      // Call API to download file with date parameters
      downloadFilteredExcel(formattedStartDate, formattedEndDate, trimmedSystemId, downloadOptions)
        .then(handleDownloadResponse)
        .catch(handleDownloadError);
    } else {
      // Call API to download file without date parameters
      // Use undefined for dates to ensure they're omitted from the request
      downloadFilteredExcel(undefined, undefined, trimmedSystemId, downloadOptions)
        .then(handleDownloadResponse)
        .catch(handleDownloadError);
    }
    
    // Handle successful download response
    function handleDownloadResponse(response) {
      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Create a temporary <a> element to trigger download
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename based on system ID and date range (if available)
      let filename;
      if (trimmedSystemId) {
        filename = dateFilterActive
          ? `System_${trimmedSystemId}_${formattedStartDate}_to_${formattedEndDate}.xlsx`
          : `System_${trimmedSystemId}_All_Data.xlsx`;
      } else {
        filename = dateFilterActive
          ? `Treatment_Usage_${formattedStartDate}_to_${formattedEndDate}.xlsx`
          : 'Treatment_Usage_All_Data.xlsx';
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setDownloadingExcel(false);
    }
    
    // Handle download error
    function handleDownloadError(error) {
      console.error('Error downloading Excel file:', error);
      alert('Failed to download Excel file. Please try again later.');
      setDownloadingExcel(false);
    }
  };
  
  // Initialize the date range and fetch data on component mount
  // Create a ref to track if component has initialized
  const initializedRef = React.useRef(false);
  
  // Handle initial setup with proper dependencies
  React.useEffect(() => {
    // Only run this logic once during initialization
    if (!initializedRef.current) {
      initializedRef.current = true;
      
      const defaultStartDate = moment(allTimeStartDate);
      const defaultEndDate = getAllTimeEndDate();
      
      // Only set the dates if they haven't been set yet
      if (!startDate || !endDate) {
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
        setDateFilterActive(true);
        
        // Initial data fetch with 10 rows per page
        fetchData(defaultStartDate, defaultEndDate, 1, 10, '');
      }
    }
  }, [startDate, endDate, fetchData]); // Include dependencies properly

  return (
    <DashboardLayout title="Overview">
      
      <Stack
        spacing={2}
        direction="row"
        style={{ marginBottom: "30px", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {/* System ID Input */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <TextField
              label="System ID"
              size="small"
              value={systemId}
              onChange={handleSystemIdChange}
              sx={{ minWidth: 180 }}
              placeholder="Enter System ID"
            />
            <Button 
              variant="contained" 
              onClick={handleSystemIdSearch}
              size="small"
              sx={{ height: 40 }}
            >
              Search
            </Button>
          </Box>

          <FormControl sx={{ minWidth: 120 }} disabled={!dateFilterActive}>
            <InputLabel id="date-range-type-label" size="small">Date Range</InputLabel>
            <Select
              labelId="date-range-type-label"
              id="date-range-type"
              value={dateRangeType}
              label="Date Range"
              onChange={handleDateRangeTypeChange}
              size="small"
              MenuProps={{
                disableScrollLock: true,
                disablePortal: true
              }}
            >
              <MenuItem value="allTime">All Time</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="day">Day</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ minWidth: 280, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              value={dateFilterActive ? getFormattedDateRange() : "No date filter"}
              disabled={!dateFilterActive}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton 
                    onClick={handleCalendarClick} 
                    size="small"
                    sx={{ ml: 0.5 }}
                    disabled={!dateFilterActive}
                  >
                    <CalendarTodayIcon />
                  </IconButton>
                ),
                sx: { pr: 0.25 }
              }}
              placeholder="Select date range"
              sx={{ 
                mr: 0.5, 
                width: '270px', 
                minWidth: '270px',
                '& .MuiInputBase-input': {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            />
            {/* Toggle button to remove/restore date filter (only visible after search with systemId) */}
            {searchPerformed && (
              <Button
                variant="contained"
                size="small"
                onClick={dateFilterActive ? handleRemoveDateFilter : handleRestoreDateFilter}
                color="info"
                
                sx={{ 
                  height: 40,
                  minWidth: '180px', 
                  whiteSpace: 'nowrap',
                  ml: 1,
                  fontWeight: 'medium',
                  bgcolor: '#1976d2', // Explicit blue color
                  '&:hover': {
                    bgcolor: '#1565c0' // Darker blue on hover
                  }
                }}
              >
                {dateFilterActive ? "REMOVE DATE FILTER" : "RESTORE DATE FILTER"}
              </Button>
            )}
          </Box>
        </Stack>
        
        <Stack direction="row" spacing={2}>
          <Button
            onClick={handleExcelDownload}
            variant="contained"
            disabled={downloadingExcel}
            startIcon={downloadingExcel ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
          >
            Download Excel
          </Button>
        </Stack>
      </Stack>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ tableLayout: 'fixed' }}>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              size={"medium"}
            >
              <TableHead>
                <TableRow>
                  {TableRowData.map((item, index) => (
                    <TableCell key={index} align={(index === 2 || index === 3 || index === 4 || index === 5) ? "center" : "left"}>
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data.map((row, index) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TableCell>
                        <Button
                          variant="text"
                          sx={{
                            color: 'primary.main',
                            textTransform: 'none', 
                            padding: '0',
                            minWidth: 'auto',
                            fontWeight: 'normal',
                            textAlign: 'left',
                            '&:hover': {
                              textDecoration: 'underline',
                              background: 'transparent'
                            }
                          }}
                          onClick={() => {
                            // Handle both system_Id and System_Id formats
                            const systemId = row.System_Id || row.system_Id;
                            
                            // Check if date filter is active
                            if (dateFilterActive) {
                              // Format dates for the URL - safely handle null dates
                              const formattedStartDate = startDate && startDate.isValid() ? startDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
                              const formattedEndDate = endDate && endDate.isValid() ? endDate.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
                              navigate(`/systemUsageDetailPage/${systemId}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`);
                            } else {
                              // Navigate without date parameters when filter is inactive
                              navigate(`/systemUsageDetailPage/${systemId}`);
                            }
                          }}
                        >
                          {/* Handle both system_Id and System_Id formats */}
                          {row.System_Id || row.system_Id}
                        </Button>
                      </TableCell>
                      <TableCell>{getFormattedDateForTable(row.Created_Date || row.created_Date)}</TableCell>
                      <TableCell align="center">{formatLastConnectionTime(row.lastConnectionTime)}</TableCell>
                      <TableCell align="center">{row.publicIp || 'N/A'}</TableCell>
                      <TableCell align="center">{row.partialCount || 0}</TableCell>
                      <TableCell align="center">{row.treatmentCount || row.countOfSystemIds || row.file_Treatment_NS || 0}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={TableRowData.length} align="center">
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-end',
            padding: '8px'
          }}>
            <CustomRowsPerPageSelector />
            <Typography variant="body2">
              {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, totalRecords)} of {totalRecords}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: '16px' }}>
              <IconButton
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 0}
                aria-label="previous page"
              >
                <KeyboardArrowLeft />
              </IconButton>
              <IconButton
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(totalRecords / rowsPerPage) - 1}
                aria-label="next page"
              >
                <KeyboardArrowRight />
              </IconButton>
            </Box>
          </Box>
          
          {/* Date Picker Popover */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleCalendarClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: { boxShadow: '0px 1px 8px rgba(0,0,0,0.15)', borderRadius: '4px' }
            }}
          >
            <Paper sx={{ width: '300px' }}>
              <Box sx={{ p: '12px 12px 0' }}>
                {/* Start Date */}
                <Box mb={1.5}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontSize: '13px' }}>
                    Start Date
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    inputRef={startDateRef}
                    value={startDate ? startDate.format('YYYY-MM-DD') : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        const date = moment(e.target.value, 'YYYY-MM-DD', true);
                        setStartDate(date);
                      } else {
                        setStartDate(null);
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton 
                          edge="end" 
                          size="small" 
                          sx={{ mr: -1 }}
                          onClick={() => {
                            // Programmatically focus and trigger click on the hidden calendar button
                            if (startDateRef.current) {
                              startDateRef.current.focus();
                              // Try showPicker if available, otherwise simulate click on the calendar button
                              try {
                                if (typeof startDateRef.current.showPicker === 'function') {
                                  startDateRef.current.showPicker();
                                } else {
                                  // For browsers without showPicker, simulate click on the picker indicator
                                  const clickEvent = new MouseEvent('click', {
                                    bubbles: true,
                                    cancelable: true,
                                    view: window
                                  });
                                  startDateRef.current.dispatchEvent(clickEvent);
                                }
                              } catch (err) {
                                // Silently handle the error
                              }
                            }
                          }}
                        >
                          <CalendarTodayIcon fontSize="small" />
                        </IconButton>
                      ),
                      sx: {
                        '& input::-webkit-calendar-picker-indicator': {
                          opacity: 0,
                          position: 'absolute',
                          right: '0',
                          width: '40px',
                          height: '40px',
                          zIndex: 1
                        }
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px'
                      }
                    }}
                  />
                </Box>
                
                {/* End Date */}
                <Box mb={1.5}>
                  <Typography variant="body2" sx={{ mb: 0.5, fontSize: '13px' }}>
                    End Date
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    inputRef={endDateRef}
                    value={endDate ? endDate.format('YYYY-MM-DD') : ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        const date = moment(e.target.value, 'YYYY-MM-DD', true);
                        setEndDate(date);
                      } else {
                        setEndDate(null);
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton 
                          edge="end" 
                          size="small" 
                          sx={{ mr: -1 }}
                          onClick={() => {
                            // Programmatically focus and trigger click on the hidden calendar button
                            if (endDateRef.current) {
                              endDateRef.current.focus();
                              // Try showPicker if available, otherwise simulate click on the calendar button
                              try {
                                if (typeof endDateRef.current.showPicker === 'function') {
                                  endDateRef.current.showPicker();
                                } else {
                                  // For browsers without showPicker, simulate click on the picker indicator
                                  const clickEvent = new MouseEvent('click', {
                                    bubbles: true,
                                    cancelable: true,
                                    view: window
                                  });
                                  endDateRef.current.dispatchEvent(clickEvent);
                                }
                              } catch (err) {
                                // Silently handle the error
                              }
                            }
                          }}
                        >
                          <CalendarTodayIcon fontSize="small" />
                        </IconButton>
                      ),
                      sx: {
                        '& input::-webkit-calendar-picker-indicator': {
                          opacity: 0,
                          position: 'absolute',
                          right: '0',
                          width: '40px',
                          height: '40px',
                          zIndex: 1
                        }
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px'
                      }
                    }}
                  />
                </Box>
              </Box>
              {/* Apply Button */}
              <Box sx={{ p: 2, pb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={handleApplyDates}
                  sx={{ 
                    py: 1, 
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    fontWeight: 500,
                    bgcolor: '#1976d2',
                    '&:hover': {
                      bgcolor: '#1565c0'
                    }
                  }}
                >
                  APPLY
                </Button>
              </Box>
            </Paper>
          </Popover>
        </>
      )}
    </DashboardLayout>
  );
};

export default InvoiceRules;
